import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import useMeasure from 'react-use-measure';
import { KeyFactorsGradedImpactDataPoint } from 'apps/sigma2-ui/src/components/key-factors/types';
import { Card } from 'apps/sigma2-ui/src/components/ui/Card';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';

export interface CardMeasurement {
  top: number;
  width: number;
  height: number;
}

export interface OnMeasureProps {
  index: number;
  measurement: CardMeasurement;
}

export interface RadialImpactChartCalloutCardProps {
  ring: KeyFactorsGradedImpactDataPoint;
  index: number;
  onMeasure: ({ index, measurement }: OnMeasureProps) => void;
  isTraitHovered: boolean[];
  setIsTraitHovered: (index: number, next: boolean) => void;
  handleSelectTrait: (controlVariableColumnHeaderValue: string) => void;
}

export const RadialImpactChartCalloutCard: React.FC<
  RadialImpactChartCalloutCardProps
> = ({
  ring,
  index,
  onMeasure,
  isTraitHovered,
  setIsTraitHovered,
  handleSelectTrait,
}) => {
  const [cardRef, cardMeasurement] = useMeasure();

  useLayoutEffect(() => {
    if (cardMeasurement.height > 0) {
      onMeasure({
        index,
        measurement: {
          top: cardMeasurement.top,
          height: cardMeasurement.height,
          width: cardMeasurement.width,
        },
      });
    }
  }, [
    cardMeasurement.height,
    cardMeasurement.top,
    cardMeasurement.width,
    index,
    onMeasure,
  ]);

  const isHovered = useMemo(
    () => isTraitHovered[index],
    [index, isTraitHovered],
  );

  const setIsHovered = useCallback(
    (hovered: boolean) => setIsTraitHovered(index, hovered),
    [index, setIsTraitHovered],
  );

  const cardClassName = () => {
    switch (ring.grade) {
      case 'high':
        return `border-1 ${isTraitHovered[index] ? ' border-royal-700' : 'border-transparent'}`;
      case 'low':
        return `border-1 ${isHovered ? ' border-gray-700' : 'border-transparent'}`;
      case 'medium':
        return `border-1 ${isHovered ? ' border-violet-700' : 'border-transparent'}`;
      default:
        throw guardExhaustive(ring.grade);
    }
  };

  const cardBackgroundClassName = () => {
    switch (ring.grade) {
      case 'high':
        return isHovered ? 'bg-[#DFE6F9]' : 'bg-white';
      case 'low':
        return isHovered ? 'bg-[#E7E8E8]' : 'bg-white';
      case 'medium':
        return isHovered ? 'bg-[#EFE2FD]' : 'bg-white';
      default:
        throw guardExhaustive(ring.grade);
    }
  };

  const cardHeadingColor = () => {
    if (!isHovered) {
      return 'royal-900';
    }

    switch (ring.grade) {
      case 'high':
        return 'royal-900';
      case 'low':
        return 'gray-900';
      case 'medium':
        return 'violet-900';
      default:
        throw guardExhaustive(ring.grade);
    }
  };

  const navigateToTrait = useCallback(() => {
    handleSelectTrait(ring.controlVariableColumnHeaderValue);
  }, [handleSelectTrait, ring.controlVariableColumnHeaderValue]);

  return (
    <TouchTarget
      behavior={{
        type: 'onClick',
        onClick: navigateToTrait,
      }}
      content={() => (
        <div
          ref={cardRef}
          className="w-fit"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Card
            paddingClassName="py-3 px-4"
            className={cardClassName()}
            backgroundClassName={cardBackgroundClassName()}
          >
            <S2Typography
              as="h4"
              variant={'body-6-bold'}
              textColorVariant={cardHeadingColor()}
              translatedContent={ring.keyFactorImpactValue}
            />
            <S2Typography
              variant={'body-6'}
              textColorVariant="gray-800"
              translatedContent={ring.keyFactorImpactLabel}
            />
          </Card>
        </div>
      )}
    />
  );
};
